import { type Course } from '@/components/CourseCard';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import useThemeColors from '@/hooks/useThemeColors';
import AppLayout from '@/layouts/AppLayout';
import { CertificateSummary, User } from '@/types';
import { router, usePage } from '@inertiajs/react';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Billing from './tabs/Billing';
import Certificates from './tabs/Certificates';
import Courses from './tabs/Courses';
import Ficha from './tabs/Ficha';
import Profile from './tabs/Profile';

interface Category {
    id: number;
    name: string;
    courses: Course[];
}

interface BillingOrder {
    order_id: string;
}

interface FichaCategory {
    category: string;
    courses: {
        course: { id: number; title: string };
        progress_percentage: number;
        total_classes: number;
        completed_classes_count: number;
        evaluation_attempts: {
            id: number;
            evaluation_title: string;
            score: number;
            total_score: number;
            percentage: number;
            passed: boolean;
            completed_at: string | null;
        }[];
        certificates: {
            id: number;
            image_url: string | null;
            issued_at: string | null;
            evaluation: { id: number; title: string } | null;
        }[];
        completed_classes: { class_title: string; completed_at: string }[];
        assignments: {
            id: number;
            assignment_title: string;
            status: string;
            teacher_comment: string;
            reviewed_at: string | null;
            reviewer_name: string | null;
        }[];
    }[];
}

interface Props {
    user: User;
    categories_with_courses?: Category[];
    certificates?: CertificateSummary[];
    has_billing_orders?: boolean;
    billing_orders?: BillingOrder[];
    billing_description?: {
        description: string | null;
        bunny_url: string | null;
    } | null;
    ficha?: FichaCategory[];
    [key: string]: unknown;
}

const MyAccountContent: React.FC<Props> = ({ user, has_billing_orders }) => {
    const { t } = useTranslation();
    const { props } = usePage<Props>();
    const colors = useThemeColors();

    // Get initial tab from URL parameter
    const getInitialTab = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const tab = urlParams.get('tab');
        return tab &&
            [
                'profile',
                'courses',
                'forums',
                'certificates',
                'billing',
                'ficha',
            ].includes(tab)
            ? tab
            : 'profile';
    };

    const [activeKey, setActiveKey] = useState(getInitialTab());

    // Update URL when tab changes
    const handleTabChange = (eventKey: string) => {
        setActiveKey(eventKey);
        // Use Inertia router to navigate with the tab parameter
        const url = new URL(window.location.href);
        if (eventKey === 'profile') {
            url.searchParams.delete('tab');
        } else {
            url.searchParams.set('tab', eventKey);
        }

        // Use Inertia's router.visit to preserve state and load data
        const partialReloadMap: Record<string, string[]> = {
            courses: ['categories_with_courses'],
            certificates: ['certificates'],
            billing: ['billing_orders', 'billing_description'],
            ficha: ['ficha'],
        };

        router.visit(url.pathname + url.search, {
            preserveState: true,
            preserveScroll: true,
            only: partialReloadMap[eventKey] ?? [],
        });
    };

    // Listen for browser back/forward buttons
    useEffect(() => {
        const handlePopState = () => {
            setActiveKey(getInitialTab());
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    return (
        <AppLayout>
            <PageTitle title="Mi Cuenta" />
            <div className="min-h-screen py-8">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                    <div className="mb-8">
                        <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">
                            {t('userAccount.myAccount')}
                        </h1>
                    </div>

                    <div className="mb-8 border-b border-gray-200 dark:border-neutral-700">
                        <nav className="-mb-px flex flex-wrap gap-x-6 gap-y-2 sm:gap-x-8">
                            <button
                                onClick={() => handleTabChange('profile')}
                                className={clsx(
                                    'border-b-2 px-1 py-4 text-sm font-medium whitespace-nowrap transition-colors',
                                    activeKey === 'profile'
                                        ? `border-${colors.primary} text-${colors.primary}`
                                        : 'border-transparent text-gray-500 dark:text-neutral-400 hover:border-gray-300 dark:hover:border-neutral-600 hover:text-gray-700 dark:hover:text-neutral-200'
                                )}
                            >
                                {t('myAccount.profile')}
                            </button>
                            <button
                                onClick={() => handleTabChange('courses')}
                                className={clsx(
                                    'border-b-2 px-1 py-4 text-sm font-medium whitespace-nowrap transition-colors',
                                    activeKey === 'courses'
                                        ? `border-${colors.primary} text-${colors.primary}`
                                        : 'border-transparent text-gray-500 dark:text-neutral-400 hover:border-gray-300 dark:hover:border-neutral-600 hover:text-gray-700 dark:hover:text-neutral-200'
                                )}
                            >
                                {t('myAccount.courses')}
                            </button>
                            <button
                                onClick={() => handleTabChange('certificates')}
                                className={clsx(
                                    'border-b-2 px-1 py-4 text-sm font-medium whitespace-nowrap transition-colors',
                                    activeKey === 'certificates'
                                        ? `border-${colors.primary} text-${colors.primary}`
                                        : 'border-transparent text-gray-500 dark:text-neutral-400 hover:border-gray-300 dark:hover:border-neutral-600 hover:text-gray-700 dark:hover:text-neutral-200'
                                )}
                            >
                                {t('myAccount.certificates')}
                            </button>
                            {has_billing_orders && (
                                <button
                                    onClick={() => handleTabChange('billing')}
                                    className={clsx(
                                        'border-b-2 px-1 py-4 text-sm font-medium whitespace-nowrap transition-colors',
                                        activeKey === 'billing'
                                            ? `border-${colors.primary} text-${colors.primary}`
                                            : 'border-transparent text-gray-500 dark:text-neutral-400 hover:border-gray-300 dark:hover:border-neutral-600 hover:text-gray-700 dark:hover:text-neutral-200'
                                    )}
                                >
                                    {t('myAccount.billing')}
                                </button>
                            )}
                            <button
                                onClick={() => handleTabChange('ficha')}
                                className={clsx(
                                    'border-b-2 px-1 py-4 text-sm font-medium whitespace-nowrap transition-colors',
                                    activeKey === 'ficha'
                                        ? `border-${colors.primary} text-${colors.primary}`
                                        : 'border-transparent text-gray-500 dark:text-neutral-400 hover:border-gray-300 dark:hover:border-neutral-600 hover:text-gray-700 dark:hover:text-neutral-200'
                                )}
                            >
                                Ficha del alumno
                            </button>
                        </nav>
                    </div>

                    <div>
                        {activeKey === 'profile' && <Profile />}
                        {activeKey === 'courses' && (
                            <Courses
                                user={user}
                                categories_with_courses={
                                    props.categories_with_courses
                                }
                            />
                        )}
                        {activeKey === 'certificates' && (
                            <Certificates certificates={props.certificates} />
                        )}
                        {activeKey === 'billing' && (
                            <Billing
                                billing_orders={props.billing_orders}
                                billing_description={props.billing_description}
                            />
                        )}
                        {activeKey === 'ficha' && (
                            <Ficha ficha={props.ficha ?? []} />
                        )}
                    </div>
                </div>
            </div>
        </AppLayout>
    );
};

const MyAccount: React.FC<Props> = ({ user, has_billing_orders }) => {
    return (
        <ThemeWrapper>
            <MyAccountContent
                user={user}
                has_billing_orders={has_billing_orders}
            />
        </ThemeWrapper>
    );
};

export default MyAccount;
